import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff, Lock, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/Button.js';
import { Checkbox, Input } from '@/components/ui/Field.js';
import { ApiError, NetworkError } from '@/lib/api-error.js';
import { landingPath } from '@/app/navigation.js';
import { useAuth } from './auth-context.js';

/**
 * Login formasining TO'RT holati (D1, B6 DoD):
 *   `idle`    — oddiy
 *   `loading` — yuborilmoqda
 *   `error`   — «Elektron pochta yoki parol noto'g'ri»
 *   `locked`  — «5 marta noto'g'ri urinish. 15 daqiqadan keyin qayta urining»
 */
type FormState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'locked'; message: string };

export function LoginPage() {
  const { user, ready, login } = useAuth();
  const [state, setState] = useState<FormState>({ kind: 'idle' });
  const [showPassword, setShowPassword] = useState(false);

  if (ready && user) return <Navigate to={landingPath(user.role)} replace />;

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get('email') ?? '').trim();
    const password = String(form.get('password') ?? '');

    setState({ kind: 'loading' });
    try {
      await login(email, password);
    } catch (error) {
      setState(toFormState(error));
    }
  };

  const locked = state.kind === 'locked';

  /*
    MAKETDAN (`D1`, KIRISH SAHIFASI): to'rli fon, o'rtada 420px
    qora chegarali oq quti. Sarlavha QUTI ICHIDA — maketda u
    kartochkaning bir qismi, ustida turgan alohida blok emas.
  */
  return (
    <div className="login-grid flex min-h-screen items-center justify-center bg-n-50 p-4">
      <div className="w-full max-w-[420px] border border-n-900 bg-white">
        <div className="flex flex-col items-center gap-3.5 px-6 pt-8 md:px-[34px]">
          <img src="/logo-mark.svg" alt="" className="size-11" />
          <div className="flex flex-col items-center gap-1.5">
            <h1 className="text-[22px] font-semibold tracking-[-0.015em] text-n-900">
              Boshqaruv paneli
            </h1>
            <p className="text-center text-[13.5px] leading-normal text-n-600">
              DUK «Geoinnovatsiya markazi»
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4 px-6 pt-6 pb-8 md:px-[34px] md:pb-[34px]">
          {state.kind === 'error' && (
            <Alert tone="error" icon={<AlertCircle className="size-4" aria-hidden />}>
              {state.message}
            </Alert>
          )}

          {locked && (
            <Alert tone="locked" icon={<ShieldAlert className="size-4" aria-hidden />}>
              {state.message}
            </Alert>
          )}

          <Input
            name="email"
            type="email"
            label="Elektron pochta"
            placeholder="admin@geoic.uz"
            autoComplete="username"
            required
            autoFocus
            disabled={locked}
          />

          <Input
            name="password"
            type={showPassword ? 'text' : 'password'}
            label="Parol"
            autoComplete="current-password"
            required
            disabled={locked}
            suffix={
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? 'Parolni yashirish' : 'Parolni ko‘rsatish'}
                className="p-1 text-n-400 hover:text-n-600"
              >
                {showPassword ? (
                  <EyeOff className="size-4" aria-hidden />
                ) : (
                  <Eye className="size-4" aria-hidden />
                )}
              </button>
            }
          />

          <Checkbox
            name="remember"
            label="Meni eslab qol"
            hint="Seans 7 kun davom etadi"
            defaultChecked
            disabled={locked}
          />

          <Button
            type="submit"
            className="w-full"
            size="lg"
            loading={state.kind === 'loading'}
            disabled={locked}
          >
            Kirish
          </Button>

          {/* Maketda izoh ustki chiziq bilan ajratilgan */}
          <p className="flex items-start gap-2.5 border-t border-n-100 pt-3.5 text-xs leading-normal text-n-600">
            <Lock className="mt-0.5 size-4 shrink-0 text-n-400" aria-hidden />
            Sahifa robot tekshiruvi bilan himoyalangan. Ketma-ket 5 marta noto‘g‘ri urinishdan so‘ng
            hisob 15 daqiqaga bloklanadi.
          </p>
        </form>
      </div>
    </div>
  );
}

function Alert({
  tone,
  icon,
  children,
}: {
  tone: 'error' | 'locked';
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  /*
    Maketda xato TO'LIQ chegarali quti (chap chiziqli emas): matn ham,
    ikonka ham xato rangida. Bloklangan holat sariq — u boshqa
    ma'noni bildiradi va bir xil ko'rinmasligi kerak.
  */
  const styles =
    tone === 'locked'
      ? 'border-warning-600 bg-warning-50 text-warning-600'
      : 'border-error-600 bg-error-50 text-error-600';

  return (
    <div
      role="alert"
      className={`flex items-start gap-2.5 border p-3.5 text-[13.5px] leading-normal ${styles}`}
    >
      <span className="mt-px shrink-0">{icon}</span>
      <span>{children}</span>
    </div>
  );
}

/**
 * Xatoni forma holatiga o'giradi.
 *
 * Server `code` beradi, matn emas — interfeys shunga qarab holat tanlaydi
 * (docs/07-API.md §1). `ACCOUNT_LOCKED` da forma butunlay bloklanadi:
 * yana urinish baribir foyda bermaydi.
 */
export function toFormState(error: unknown): FormState {
  if (error instanceof ApiError) {
    if (error.code === 'ACCOUNT_LOCKED') return { kind: 'locked', message: error.message };
    if (error.code === 'USER_BLOCKED') {
      return { kind: 'locked', message: 'Hisob bloklangan. Administratorga murojaat qiling' };
    }
    if (error.code === 'INVALID_CREDENTIALS') {
      return { kind: 'error', message: 'Elektron pochta yoki parol noto‘g‘ri' };
    }
    if (error.code === 'VALIDATION_FAILED') {
      return { kind: 'error', message: 'Elektron pochta yoki parol formati noto‘g‘ri' };
    }
    return { kind: 'error', message: error.message };
  }

  if (error instanceof NetworkError) return { kind: 'error', message: error.message };
  return { kind: 'error', message: 'Kutilmagan xato. Qaytadan urinib ko‘ring' };
}
